// Table Server Client - Routes table requests to main PC or local server
import axios from 'axios';
import { Storage } from '@/services/Storage';
import connectivityMonitor from '@/services/connectivityMonitor';
import { getPlatform } from '@/utils/platformDetection';
import androidTableServerBridge from '@/services/androidTableServerBridge';

const TABLE_SERVER_PORT = 3500;
const REQUEST_TIMEOUT = 5000; // 5 seconds
const PROXY_INVOICE_TIMEOUT = 210000; // allow backend invoice processing via main PC
let lastConnectionIssue = null;

function getAndroidHostPort(androidSettings = null) {
    const parsed = Number(androidSettings?.androidHostPort || TABLE_SERVER_PORT);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : TABLE_SERVER_PORT;
}

function getAndroidLocalServerUrl(androidSettings = null) {
    return `http://localhost:${getAndroidHostPort(androidSettings)}`;
}

function parseMainPCTarget(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value) return null;

    // Accept bare host/IP, host:port, or full URL entered by the user.
    const candidate = /^https?:\/\//i.test(value) ? value : `http://${value}`;
    try {
        const parsed = new URL(candidate);
        const hostname = String(parsed.hostname || '').trim();
        if (!hostname) return null;
        const portFromInput = Number(parsed.port || 0);
        const port = Number.isFinite(portFromInput) && portFromInput > 0 ? portFromInput : TABLE_SERVER_PORT;
        return { hostname, port };
    } catch (_) {
        return null;
    }
}

function buildMainPCBaseUrl(rawValue) {
    const target = parseMainPCTarget(rawValue);
    if (!target) return null;
    return `http://${target.hostname}:${target.port}`;
}

function normalizeHostName(rawValue) {
    const target = parseMainPCTarget(rawValue);
    if (target?.hostname) return String(target.hostname).trim().toLowerCase();
    const value = String(rawValue || '').trim().toLowerCase();
    return value || '';
}

async function getElectronLocalHostnames() {
    const hostnames = new Set(['localhost', '127.0.0.1']);

    try {
        const pcName = await window?.hardwareIPC?.getPcName?.();
        const normalizedName = normalizeHostName(pcName);
        if (normalizedName) hostnames.add(normalizedName);
    } catch (_) { }

    try {
        const pcIp = await window?.hardwareIPC?.getPcIP?.();
        const normalizedIp = normalizeHostName(pcIp);
        if (normalizedIp) hostnames.add(normalizedIp);
    } catch (_) { }

    return hostnames;
}

function describeConnectionIssue(error, host) {
    const code = error?.code || '';
    const rawMsg = String(error?.message || 'Unknown network error');
    const message = rawMsg.toLowerCase();
    if (code === 'ENOTFOUND' || message.includes('err_name_not_resolved') || message.includes('name not resolved')) {
        return `Cannot resolve Main PC name "${host}". Check the Main PC name in settings or use a reachable host/IP.`;
    }
    if (code === 'ECONNABORTED' || message.includes('timeout')) {
        return `Connection to Main PC "${host}" timed out after ${REQUEST_TIMEOUT}ms.`;
    }
    if (message.includes('network error')) {
        return `Network error while reaching Main PC "${host}". Ensure both devices are on the same LAN.`;
    }
    return `Failed to reach Main PC "${host}": ${rawMsg}`;
}

function getRequestTimeout(endpoint) {
    if (endpoint === '/proxy/invoice/mobile') return PROXY_INVOICE_TIMEOUT;
    return REQUEST_TIMEOUT;
}

/**
 * Check if current PC is the main table server
 */
async function isMainPC() {
    try {
        const platform = getPlatform();
        if (platform === 'android') {
            const androidSettings = await Storage.getAndroidSettings();
            return androidSettings?.androidHostEnabled === true;
        }

        const settings = await Storage.getDeviceSettings();
        if (!settings || !settings.server) {
            return false;
        }
        const configuredHost = normalizeHostName(settings.server);
        if (!configuredHost) return false;

        const storedHost = normalizeHostName(settings.PC);
        if (storedHost && configuredHost === storedHost) {
            return true;
        }

        const localHostnames = await getElectronLocalHostnames();
        return localHostnames.has(configuredHost);
    } catch (error) {
        console.error('[TableServerClient] Error checking if main PC:', error);
        return false;
    }
}

/**
 * Get main PC URL
 * Android reads from Android-specific storage, Electron reads from device settings
 */
async function getMainPCUrl() {
    const platform = getPlatform();

    if (platform === 'android') {
        const androidSettings = await Storage.getAndroidSettings();
        if (androidSettings?.androidHostEnabled === true) {
            return getAndroidLocalServerUrl(androidSettings);
        }

        // Android: Prefer Android-local setting, then fall back to legacy/shared settings.
        const deviceSettings = await Storage.getDeviceSettings();
        const candidates = [
            androidSettings?.mainPCName,
            androidSettings?.mainTableServerIP,
            deviceSettings?.mainTableServerIP,
            deviceSettings?.server
        ];

        let selectedUrl = null;
        for (const candidate of candidates) {
            const url = buildMainPCBaseUrl(candidate);
            if (url) {
                selectedUrl = url;
                break;
            }
        }

        if (!selectedUrl) {
            throw new Error('Main PC name not configured. Please enter Main PC name in Table Server Settings.');
        }

        return selectedUrl;
    }

    // Electron: Read from device settings (server field)
    try {
        const settings = await Storage.getDeviceSettings();
        if (!settings || !settings.server) {
            throw new Error('Main PC not configured in device settings');
        }
        const parsedUrl = buildMainPCBaseUrl(settings.server);
        if (!parsedUrl) {
            throw new Error('Main PC host is invalid in device settings');
        }
        return parsedUrl;
    } catch (error) {
        console.error('[TableServerClient] Error getting main PC URL:', error);
        throw error;
    }
}

/**
 * Check if main PC is reachable on LAN
 */
async function checkMainPCConnection() {
    try {
        const platform = getPlatform();
        if (platform === 'android') {
            const androidSettings = await Storage.getAndroidSettings();
            if (androidSettings?.androidHostEnabled === true) {
                try {
                    await ensureAndroidHostRunning(androidSettings);
                } catch (hostError) {
                    lastConnectionIssue = {
                        code: 'HOST_START_FAILED',
                        message: hostError?.message || 'Failed to start Android host server',
                        host: 'localhost',
                        reason: `This tablet is set as Main Host but local server could not start: ${hostError?.message || 'Unknown error'}`
                    };
                    connectivityMonitor.setOnline(false);
                    return false;
                }
            }
        }

        const url = await getMainPCUrl();
        const host = String(new URL(url).hostname || '');
        const response = await axios.get(`${url}/health`, {
            timeout: REQUEST_TIMEOUT
        });
        const health = response?.data || {};
        const lanReachable = health.ok === true;
        const backendReachable = health.backendReachable !== false;

        // Online/offline follows backend reachability, not LAN.
        connectivityMonitor.setOnline(backendReachable);

        if (lanReachable && !backendReachable) {
            lastConnectionIssue = {
                code: 'BACKEND_OFFLINE',
                message: 'Main PC can be reached but backend is offline',
                host,
                reason: `Main PC "${host}" is reachable, but backend is unreachable.`
            };
        } else {
            lastConnectionIssue = null;
        }

        return lanReachable;
    } catch (error) {
        // If we are Main PC and hostname failed, try localhost
        if (await isMainPC()) {
            try {
                let localUrl = `http://localhost:${TABLE_SERVER_PORT}`;
                if (getPlatform() === 'android') {
                    const androidSettings = await Storage.getAndroidSettings();
                    localUrl = getAndroidLocalServerUrl(androidSettings);
                }
                const response = await axios.get(`${localUrl}/health`, {
                    timeout: REQUEST_TIMEOUT
                });
                const health = response?.data || {};
                const lanReachable = health.ok === true;
                const backendReachable = health.backendReachable !== false;
                connectivityMonitor.setOnline(backendReachable);
                if (lanReachable && !backendReachable) {
                    lastConnectionIssue = {
                        code: 'BACKEND_OFFLINE',
                        message: 'Main PC (localhost) reachable but backend is offline',
                        host: 'localhost',
                        reason: 'Main PC is reachable, but backend is unreachable.'
                    };
                } else {
                    lastConnectionIssue = null;
                }
                return lanReachable;
            } catch (localError) {
                console.error('[TableServerClient] Localhost fallback also failed:', localError.message);
            }
        }
        try {
            const backendReachable = await connectivityMonitor.testConnectivity();
            connectivityMonitor.setOnline(backendReachable);
        } catch (_) {
            // If testConnectivity fails unexpectedly, default to offline
            connectivityMonitor.setOnline(false);
        }
        let host = '';
        try {
            const mainUrl = await getMainPCUrl();
            host = String(new URL(mainUrl).hostname || '');
        } catch (_) { }
        lastConnectionIssue = {
            code: error?.code || null,
            message: error?.message || 'Unknown network error',
            host,
            reason: describeConnectionIssue(error, host || 'unknown-host')
        };
        console.error('[TableServerClient] Main PC not reachable:', error.message);
        return false;
    }
}

async function ensureAndroidHostRunning(androidSettings = null) {
    const settings = androidSettings || await Storage.getAndroidSettings();
    if (settings?.androidHostEnabled !== true) return { ok: false, skipped: true };

    const status = await androidTableServerBridge.getStatus();
    if (status?.ok && status?.isRunning) {
        return status;
    }

    const startResult = await androidTableServerBridge.startServer({
        port: getAndroidHostPort(settings),
        backendUrl: String(window?.serverApi || '').trim()
    });
    if (!startResult?.ok) {
        throw new Error(startResult?.error || 'Failed to start Android table server');
    }
    return startResult;
}

/**
 * Map table server endpoint to backend API endpoint
 * @param {string} endpoint - Table server endpoint
 * @param {string} storeDB - Store database name
 * @param {string} user - Username (for GET /tables/all)
 * @param {string} saleNumber - Sale number (for specific table endpoints)
 * @returns {string} Backend API endpoint
 */
function mapToBackendEndpoint(endpoint, storeDB, user = null, saleNumber = null) {
    // Map table server endpoints to backend API endpoints
    const mapping = {
        '/tables/all': user ? `tables/${storeDB}/${user}` : `tables/${storeDB}/getAllOpenTables`,
        '/tables/create': `tables/${storeDB}`,
        '/tables/update': `tables/${storeDB}`,
        '/tables/addOrUpdate': `tables/${storeDB}`,
        '/tables/merge': `tables/${storeDB}/merge`,
        '/tables/split': `tables/${storeDB}/split`,
        '/tables/transfer': `tables/${storeDB}/transfer`,
        '/tables/initiateEmptyTable': `tables/${storeDB}/initiateEmptyTable`,
        '/proxy/invoice/mobile': `invoice/${storeDB}/mobile?android=true`,
        '/proxy/invoice/salenum': `invoice/${storeDB}/salenum`,
        '/proxy/tables/sendToRoom': `tables/${storeDB}/sendToRoom`,
        '/proxy/supervisorOverrides/logItemVoid': `supervisorOverrides/${storeDB}/logItemVoid`,
    };

    // Handle dynamic endpoints
    if (endpoint.startsWith('/tables/void/')) {
        const sn = saleNumber || endpoint.split('/')[3];
        return `tables/${storeDB}/void/${sn}`;
    }
    if (endpoint.startsWith('/tables/count/')) {
        const username = endpoint.split('/')[3];
        return `tables/${storeDB}/count/${username}`;
    }
    if (endpoint.match(/^\/tables\/\d+$/)) {
        const sn = saleNumber || endpoint.split('/')[2];
        return `savetable/${storeDB}/${sn}`;
    }
    if (endpoint.startsWith('/proxy/tables/open/')) {
        const sn = saleNumber || endpoint.split('/')[4];
        return `tables/${storeDB}/openTable/${sn}`;
    }
    if (endpoint.startsWith('/proxy/invoice/getInvNum/')) {
        const invoice = endpoint.split('/')[4];
        return `invoice/${storeDB}/getInvNum?invoice=${encodeURIComponent(invoice || '')}`;
    }

    return mapping[endpoint] || endpoint;
}

/**
 * Call backend API directly (Android Direct Backend Mode)
 * @param {string} endpoint - Table server endpoint
 * @param {object} payload - Request payload
 * @param {string} method - HTTP method
 * @param {string} storeDB - Store database name
 * @param {string} user - Username (for GET requests)
 * @param {string} saleNumber - Sale number (for specific endpoints)
 */
async function callBackendAPI(endpoint, payload, method, storeDB, user = null, saleNumber = null) {
    try {
        const backendEndpoint = mapToBackendEndpoint(endpoint, storeDB, user, saleNumber);
        const url = window.serverApi + backendEndpoint;

        let response;
        if (method === 'get') {
            response = await axios.get(url, { timeout: REQUEST_TIMEOUT });
        } else if (method === 'delete') {
            response = await axios.delete(url, { timeout: REQUEST_TIMEOUT });
        } else if (method === 'put') {
            response = await axios.put(url, payload, { timeout: REQUEST_TIMEOUT });
        } else {
            response = await axios.post(url, payload, { timeout: REQUEST_TIMEOUT });
        }

        return response.data;
    } catch (error) {
        console.error('[TableServerClient][Android Direct] Backend API error:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url
        });
        throw error;
    }
}

/**
 * Route table request to appropriate server
 * @param {string} endpoint - API endpoint (e.g., '/tables/create')
 * @param {object} payload - Request payload
 * @param {string} method - HTTP method (get, post, put, delete)
 * @param {string} storeDB - Store database name
 */
/**
 * Route table request to appropriate server
 * @param {string} endpoint - API endpoint (e.g., '/tables/create')
 * @param {object} payload - Request payload
 * @param {string} method - HTTP method (get, post, put, delete)
 * @param {string} storeDB - Store database name
 * @param {boolean} synced - Whether operation is already synced
 */
async function routeTableRequest(endpoint, payload, method = 'post', storeDB, synced = false, user = null, saleNumber = null) {
    try {
        const platform = getPlatform();

        // ANDROID: Check connection mode
        if (platform === 'android') {
            const androidSettings = await Storage.getAndroidSettings();
            const isAndroidHost = androidSettings?.androidHostEnabled === true;
            const useTableServer = androidSettings?.androidTableServerEnabled === true;

            if (isAndroidHost || useTableServer) {
                if (isAndroidHost) {
                    await ensureAndroidHostRunning(androidSettings);
                }

                // LAN Mode: Connect to Main PC via table server
                const queryParams = [];
                if (storeDB) queryParams.push(`storeDB=${storeDB}`);
                if (synced) queryParams.push(`synced=true`);
                const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

                const mainPCUrl = isAndroidHost ? getAndroidLocalServerUrl(androidSettings) : await getMainPCUrl();
                const fullUrl = `${mainPCUrl}${endpoint}${queryString}`;

                const response = await axios[method](fullUrl, payload, {
                    timeout: getRequestTimeout(endpoint)
                });
                return response.data;
            } else {
                // Direct Backend Mode: Connect directly to backend API
                return await callBackendAPI(endpoint, payload, method, storeDB, user, saleNumber);
            }
        }

        // ELECTRON: Existing logic (unchanged)
        const isMain = await isMainPC();
        const isOnline = connectivityMonitor.getStatus();

        // Build query string
        const queryParams = [];
        if (storeDB) queryParams.push(`storeDB=${storeDB}`);
        if (synced) queryParams.push(`synced=true`);
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

        // Main PC: Use local Electron table server
        if (isMain) {
            // Check if table server is running
            if (window?.tableServer) {
                try {
                    const status = await window.tableServer.getStatus();

                    if (!status.ok || !status.data.isRunning) {
                        // Start table server if not running
                        const startResult = await window.tableServer.start();
                        if (!startResult.ok) {
                            throw new Error('Failed to start table server: ' + startResult.error);
                        }
                    }
                } catch (ipcError) {
                    console.warn('[TableServerClient] Could not check/start table server via IPC:', ipcError);
                    // Continue anyway - server might already be running
                }
            }

            // Route to local server
            const localUrl = `http://localhost:${TABLE_SERVER_PORT}`;
        const fullUrl = `${localUrl}${endpoint}${queryString}`;

        const response = await axios[method](fullUrl, payload, {
            timeout: getRequestTimeout(endpoint)
        });

            return response.data;
        }

        // Client PC: Try to connect to main PC on LAN
        const mainPCUrl = await getMainPCUrl();
        const fullUrl = `${mainPCUrl}${endpoint}${queryString}`;

        try {
            const response = await axios[method](fullUrl, payload, {
                timeout: getRequestTimeout(endpoint)
            });
            return response.data;
        } catch (error) {
            // If main PC unreachable, throw clear error
            if (error.code === 'ECONNABORTED') {
                throw new Error('Cannot connect to main device. Please ensure main device is powered on and app is open.');
            }
            if (error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT') {
                throw new Error('Cannot connect to main device. Table operations unavailable. Please ensure main device is powered on and app is open.');
            }
            throw error;
        }
    } catch (error) {
        console.error('[TableServerClient] Route error:', error);
        throw error;
    }
}

/**
 * Get all tables
 */
export async function getAllTables(storeDB, user) {
    return routeTableRequest('/tables/all', null, 'get', storeDB, false, user);
}

/**
 * Create new table
 */
export async function createTable(payload, storeDB, synced = false) {
    return routeTableRequest('/tables/create', payload, 'post', storeDB, synced);
}

/**
 * Update existing table
 */
export async function updateTable(payload, storeDB, synced = false) {
    return routeTableRequest('/tables/update', payload, 'post', storeDB, synced);
}

/**
 * Add or update table (combined operation)
 */
export async function addOrUpdateTable(payload, storeDB) {
    // Check if synced flag is in payload
    const synced = payload.synced === true;
    if (payload.SaleNum && payload.SaleNum !== 0) {
        return updateTable(payload, storeDB, synced);
    } else {
        return createTable(payload, storeDB, synced);
    }
}

/**
 * Remove table
 */
export async function removeTable(saleNumber, storeDB, synced = false) {
    return routeTableRequest(`/tables/void/${saleNumber}`, null, 'delete', storeDB, synced);
}

/**
 * Merge tables
 */
export async function mergeTables(payload, storeDB, synced = false) {
    return routeTableRequest('/tables/merge', payload, 'post', storeDB, synced);
}

/**
 * Split table
 */
export async function splitTable(payload, storeDB, synced = false) {
    return routeTableRequest('/tables/split', payload, 'post', storeDB, synced);
}

/**
 * Transfer table
 */
export async function transferTable(payload, storeDB, synced = false) {
    return routeTableRequest('/tables/transfer', payload, 'put', storeDB, synced);
}

/**
 * Print orders
 */
export async function printOrders(payload, storeDB) {
    return routeTableRequest('/tables/printOrders', payload, 'post', storeDB);
}

/**
 * Get table by sale number
 */
export async function getTableBySaleNumber(saleNumber, storeDB) {
    return routeTableRequest(`/tables/${saleNumber}`, null, 'get', storeDB, false, null, saleNumber);
}

/**
 * Initiate empty table
 */
export async function initiateEmptyTable(payload, storeDB) {
    return routeTableRequest('/tables/initiateEmptyTable', payload, 'post', storeDB);
}

/**
 * Count open tables for user
 */
export async function countOpenTables(user, storeDB) {
    return routeTableRequest(`/tables/count/${user}`, null, 'get', storeDB, false, user);
}

/**
 * Check if main PC is reachable
 */
export async function testConnection() {
    return checkMainPCConnection();
}

export function getLastConnectionIssue() {
    return lastConnectionIssue;
}

/**
 * Check if current PC is main PC
 */
export async function checkIsMainPC() {
    return isMainPC();
}

/**
 * Binary Main PC role for UI + guards.
 * Only two outcomes matter: this device IS Main, or it must be connected to Main.
 * (Internal Android routing may still use LAN/host flags; status never says "Direct backend".)
 */
export async function getConnectionMode() {
    const main = await isMainPC();
    return main ? 'main' : 'client';
}

/**
 * Always true for non-Main devices — they must reach Main PC.
 * Main devices do not need a remote peer.
 */
export async function requiresMainPcHealth() {
    return !(await isMainPC());
}

/**
 * Display target for Main PC badge (host name/IP or "This device").
 */
export async function getMainPcDisplayTarget() {
    if (await isMainPC()) return 'This device';
    const platform = getPlatform();
    if (platform === 'android') {
        const androidSettings = await Storage.getAndroidSettings();
        const deviceSettings = await Storage.getDeviceSettings();
        return String(
            androidSettings?.mainPCName ||
            androidSettings?.mainTableServerIP ||
            deviceSettings?.mainTableServerIP ||
            deviceSettings?.server ||
            ''
        ).trim();
    }
    const settings = await Storage.getDeviceSettings();
    return String(settings?.server || '').trim();
}

/**
 * Binary connection check (Settings Test Connection / drawer / create).
 * Main → local table server health. Not Main → Main PC :3500 health.
 * @returns {{ connected: boolean, isMain: boolean, mode: string, target: string, required: boolean, issue: object|null }}
 */
export async function checkConnection() {
    const isMain = await isMainPC();
    const mode = isMain ? 'main' : 'client';
    const target = await getMainPcDisplayTarget();
    // Non-Main always requires a live Main PC. Main is always "required" only as local host health.
    const required = true;

    try {
        if (isMain) {
            try {
                if (getPlatform() === 'android') {
                    const androidSettings = await Storage.getAndroidSettings();
                    await ensureAndroidHostRunning(androidSettings);
                } else if (window?.tableServer) {
                    const status = await window.tableServer.getStatus();
                    if (!status?.ok || !status?.data?.isRunning) {
                        await window.tableServer.start();
                    }
                }
            } catch (_) { /* health below */ }
        }

        let connected = await checkMainPCConnection();

        if (!connected && isMain) {
            try {
                await startLocalServer();
                await new Promise((resolve) => setTimeout(resolve, 2000));
                connected = await checkMainPCConnection();
            } catch (_) {
                connected = false;
            }
        }

        return {
            connected: !!connected,
            isMain,
            mode,
            target: isMain ? 'This device' : target,
            required,
            issue: connected ? null : getLastConnectionIssue()
        };
    } catch (error) {
        lastConnectionIssue = {
            code: error?.code || null,
            message: error?.message || 'Unknown connection error',
            host: target || '',
            reason: error?.message || 'Failed to check Main PC connection'
        };
        return {
            connected: false,
            isMain,
            mode,
            target: isMain ? 'This device' : target,
            required,
            issue: lastConnectionIssue
        };
    }
}

/**
 * Create/save guard — binary only:
 * Main device → OK (local host). Not Main → must reach Main PC.
 */
export async function ensureMainPcReachableForTableWrite() {
    const result = await checkConnection();
    if (result.isMain || result.connected) return result;

    const issue = result.issue || getLastConnectionIssue();
    const reason = issue?.reason || 'Main device is unreachable.';
    const hostLabel = String(issue?.host || result.target || '').trim();
    const hostPart = hostLabel && hostLabel !== 'This device' ? ` Main PC: ${hostLabel}.` : '';
    throw new Error(`Main device is unreachable. Cannot complete table operation.${hostPart} ${reason}`.trim());
}

/**
 * Get tables with pending operations
 */
export async function getPendingTables(storeDB) {
    return routeTableRequest('/tables/pending', null, 'get', storeDB);
}

/**
 * Get offline activity logs
 */
export async function getTableLogs(storeDB) {
    return routeTableRequest('/tables/logs', null, 'get', storeDB);
}

export async function proxyInvoiceMobile(payload, storeDB) {
    return routeTableRequest('/proxy/invoice/mobile', payload, 'post', storeDB);
}

export async function proxyOpenTable(storeDB, saleNumber) {
    return routeTableRequest(`/proxy/tables/open/${saleNumber}`, null, 'get', storeDB, false, null, saleNumber);
}

export async function proxySendToRoom(payload, storeDB) {
    return routeTableRequest('/proxy/tables/sendToRoom', payload, 'post', storeDB);
}

export async function proxyLogItemVoid(payload, storeDB) {
    return routeTableRequest('/proxy/supervisorOverrides/logItemVoid', payload, 'post', storeDB);
}

export async function proxyGetInvoiceByNumber(storeDB, invoice) {
    return routeTableRequest(`/proxy/invoice/getInvNum/${invoice}`, null, 'get', storeDB);
}

export async function proxyGetSaleNum(storeDB) {
    return routeTableRequest('/proxy/invoice/salenum', null, 'get', storeDB);
}

/**
 * Start local table server (Main PC only)
 */
export async function startLocalServer() {
    if (window?.tableServer) {
        return window.tableServer.start();
    }
    if (getPlatform() === 'android') {
        const androidSettings = await Storage.getAndroidSettings();
        return androidTableServerBridge.startServer({
            port: getAndroidHostPort(androidSettings),
            backendUrl: String(window?.serverApi || '').trim()
        });
    }
    return { ok: false, error: 'Table server API not available' };
}

export async function stopLocalServer() {
    if (window?.tableServer) {
        return window.tableServer.stop();
    }
    if (getPlatform() === 'android') {
        return androidTableServerBridge.stopServer();
    }
    return { ok: false, error: 'Table server API not available' };
}

export async function getLocalServerInfo() {
    if (window?.tableServer) {
        return window.tableServer.getStatus();
    }
    if (getPlatform() === 'android') {
        const [status, ip] = await Promise.all([
            androidTableServerBridge.getStatus(),
            androidTableServerBridge.getLocalIp()
        ]);
        return {
            ok: Boolean(status?.ok),
            isRunning: Boolean(status?.isRunning),
            port: status?.port || TABLE_SERVER_PORT,
            localIp: ip?.localIp || ''
        };
    }
    return { ok: false, error: 'Table server API not available' };
}

export default {
    getAllTables,
    createTable,
    updateTable,
    addOrUpdateTable,
    removeTable,
    mergeTables,
    splitTable,
    transferTable,
    printOrders,
    getTableBySaleNumber,
    initiateEmptyTable,
    countOpenTables,
    testConnection,
    getLastConnectionIssue,
    checkIsMainPC,
    getConnectionMode,
    requiresMainPcHealth,
    getMainPcDisplayTarget,
    checkConnection,
    ensureMainPcReachableForTableWrite,
    startLocalServer,
    stopLocalServer,
    getLocalServerInfo,
    getPendingTables,
    getTableLogs,
    proxyInvoiceMobile,
    proxyOpenTable,
    proxySendToRoom,
    proxyLogItemVoid,
    proxyGetInvoiceByNumber,
    proxyGetSaleNum
};
